import type { LogBindings } from "@repo/logger";
import { type BrowserLogger, createBrowserLogger } from "@repo/logger/browser";

const API_BASE = process.env.BUN_PUBLIC_API_URL ?? "";
const IS_DEV = process.env.NODE_ENV !== "production";
const MIN_LEVEL = IS_DEV ? "debug" : "info";

const internalLogger = createBrowserLogger({
  endpoint: `${API_BASE}/internal/client-logs`,
  minLevel: MIN_LEVEL,
});

// Public client flow (`/p/:token/*`) has no staff session, so it ships to
// /public/client-logs with the publicSession JWT; the backend stamps
// clientId/tenantId/flowInstanceId from it. sendBeacon on unload cannot carry
// the header, so the last batch before navigation may drop — best-effort.
const publicLogger = createBrowserLogger({
  endpoint: `${API_BASE}/public/client-logs`,
  minLevel: MIN_LEVEL,
  getAuthHeader: () => {
    const token = getPublicSessionToken();
    return token ? `Bearer ${token}` : null;
  },
});

function getPublicSessionToken(): string | null {
  try {
    return localStorage.getItem("publicSessionToken");
  } catch {
    return null;
  }
}

function isPublicFlowPath(): boolean {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/p/");
}

// Picks the sink per call: on public flow pages logs go to the public endpoint
// (or are dropped before the client has authenticated — the endpoint requires
// a session); everywhere else to the internal endpoint.
function pickLogger(internal: BrowserLogger, pub: BrowserLogger): BrowserLogger | null {
  if (!isPublicFlowPath()) return internal;
  return getPublicSessionToken() ? pub : null;
}

function makeRoutingLogger(internal: BrowserLogger, pub: BrowserLogger): BrowserLogger {
  return {
    debug(obj, msg) {
      pickLogger(internal, pub)?.debug(obj, msg);
    },
    info(obj, msg) {
      pickLogger(internal, pub)?.info(obj, msg);
    },
    warn(obj, msg) {
      pickLogger(internal, pub)?.warn(obj, msg);
    },
    error(obj, msg) {
      pickLogger(internal, pub)?.error(obj, msg);
    },
    child(bindings: LogBindings) {
      return makeRoutingLogger(internal.child(bindings), pub.child(bindings));
    },
    async flush() {
      await Promise.all([internal.flush(), pub.flush()]);
    },
  };
}

export const log = makeRoutingLogger(internalLogger, publicLogger);
